import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Button, Press, ScreenHeader, EmptyState, ProgressBar } from "../ui";
import { Icon } from "../Icon";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { FLASHCARDS, shuffle, t } from "../content";

const KIND = { know: { key: "cardKnow", color: (c) => c.brandPrimary }, understand: { key: "cardUnderstand", color: (c) => c.info }, apply: { key: "cardApply", color: (c) => c.success } };

export default function Flashcards() {
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const st = useAppState();
  const deck = useMemo(() => {
    const dueIds = new Set(st.dueFlashcardIds());
    const due = FLASHCARDS.filter((c) => dueIds.has(c.id));
    return shuffle(due.length ? due : FLASHCARDS);
  }, []); // eslint-disable-line
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);

  if (done || deck.length === 0) {
    return (
      <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surface }}>
        <ScreenHeader title={tr("flashcards")} onBack={() => navigate(-1)} />
        <EmptyState icon="confetti" title={tr("noCardsDue")} />
        <View style={{ padding: 20 }}><Button title={tr("done")} onPress={() => navigate(-1)} /></View>
      </View>
    );
  }
  const card = deck[i];
  const kind = KIND[card.kind];
  const answer = (knew) => {
    st.reviewFlashcard(card.id, knew);
    if (i + 1 >= deck.length) setDone(true);
    else { setI(i + 1); setFlipped(false); }
  };

  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={tr("flashcards")} subtitle={`${i + 1} / ${deck.length}`} onBack={() => navigate(-1)} />
      <View style={{ padding: "8px 20px" }}><ProgressBar progress={(i / deck.length) * 100} /></View>
      <View style={{ flex: 1, padding: 20, justifyContent: "center" }}>
        <Press testID="flashcard" onClick={() => setFlipped((f) => !f)} style={{ minHeight: 360, maxHeight: 420, display: "flex" }}>
          <Card style={{ flex: 1, alignItems: "center", justifyContent: "center", padding: 28, position: "relative", minHeight: 360 }}>
            <View style={{ position: "absolute", top: 16, left: 16, backgroundColor: colors.surfaceSecondary, borderRadius: 999, padding: "6px 12px" }}>
              <Text style={{ fontWeight: fonts.bold, fontSize: 11, color: kind.color(colors), letterSpacing: 0.5 }}>{tr(kind.key).toUpperCase()}</Text>
            </View>
            <Text style={{ fontWeight: fonts.semibold, fontSize: 12, color: colors.muted, marginBottom: 14 }}>
              {flipped ? (lang === "de" ? "ANTWORT" : "ANSWER") : (lang === "de" ? "FRAGE" : "QUESTION")}
            </Text>
            <Text style={{ fontWeight: flipped ? fonts.regular : fonts.bold, fontSize: flipped ? 17 : 20, lineHeight: flipped ? "26px" : "28px", color: colors.onSurface, textAlign: "center" }}>
              {flipped ? t(card.back, lang) : t(card.front, lang)}
            </Text>
            {!flipped && (
              <View row style={{ alignItems: "center", gap: 6, marginTop: 24 }}>
                <Icon name="eye" size={18} color={colors.brandPrimary} />
                <Text style={{ fontWeight: fonts.medium, fontSize: 14, color: colors.brandPrimary }}>{tr("showAnswer")}</Text>
              </View>
            )}
          </Card>
        </Press>
      </View>
      <View row style={{ gap: 12, padding: "0 20px 16px" }}>
        <Button testID="card-again" title={tr("practiceAgain")} variant="secondary" icon="arrow-right" style={{ flex: 1 }} onPress={() => answer(false)} />
        <Button testID="card-knew" title={tr("knewIt")} icon="check" style={{ flex: 1 }} onPress={() => answer(true)} />
      </View>
    </View>
  );
}
